import { Inject, Injectable } from '@angular/core';
import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';
import { TranslateService } from '@ngx-translate/core';
import { Observable } from 'rxjs';
import { catchError, map, mergeMap, startWith } from 'rxjs/operators';
import { ENDPOINTS } from '../../enums/endpoints.enum';
import { EquipmentModel } from '../../models/complex/equipment.model';
import { TableModel } from '../../models/complex/table.model';
import { TableModelOptions } from '../../models/options/table-model-options';
import { ColumnModel } from '../../models/primitive/column.model';
import { isDefined } from '../../utilities/is-defined';
import { OnlineOnlyService } from '../online-only/online-only.service';
import { UriUtilitiesService } from '../uri-utilities/uri-utilities.service';
import { SoftwareLicencesInterface } from '../../interfaces/software-licences.interface';
import { ODataSoftwareLicenceSet, SoftwareLicenceModel } from './software-licence.model';
import { SoftwareLicencesStorageService } from './software-licences-storage.service';

@Injectable()
export class SoftwareLicencesService implements SoftwareLicencesInterface {
    public constructor(
        @Inject(LogInstanceToken) private readonly log: LogInterface,
        private readonly softwareLicencesStorageService: SoftwareLicencesStorageService,
        private readonly onlineOnlyService: OnlineOnlyService,
        private readonly uriUtilities: UriUtilitiesService,
        private readonly translate: TranslateService,
    ) {}

    public getTableModelForSoftwareLicences(
        mainEquipment: EquipmentModel,
    ): Observable<TableModel<SoftwareLicenceModel>> {
        const columns: ColumnModel[] = [
            new ColumnModel(
                <string>this.translate.instant('NOTIFICATION_DETAIL_EQUIPMENTS_CONFIGURATION_DESCRIPTION'),
                'description',
            ),
            new ColumnModel(
                <string>this.translate.instant('NOTIFICATION_DETAIL_EQUIPMENTS_CONFIGURATION_MATERIAL_ID'),
                'materialNumber',
            ),
        ];

        const tableOptions = new TableModelOptions({ columns });

        return this.getSoftwareLicencesForEquipment(mainEquipment).pipe(
            startWith([]),
            map((licences: SoftwareLicenceModel[]) => new TableModel(licences, tableOptions)),
        );
    }

    public getSoftwareLicencesForEquipment(equipment: EquipmentModel): Observable<SoftwareLicenceModel[]> {
        let query = ENDPOINTS.SoftwareLicences.toString();
        const filter = `Material eq '${encodeURIComponent(
            equipment.matnr.value,
        )}' and SerialNo eq '${encodeURIComponent(equipment.sernr.value)}'`;
        query = this.uriUtilities.appendFilterValue(query, filter);
        query = this.uriUtilities.appendParameter(query, '$format=json');

        return this.onlineOnlyService.get(query).pipe(
            mergeMap((data: ODataSoftwareLicenceSet[]) =>
                this.softwareLicencesStorageService.saveSoftwareLicencesToCache(equipment, data),
            ),
            catchError((error) => {
                // assume we are offline and try to load from cache
                this.log.debug(
                    `SoftwareLicencesService: getSoftwareLicencesForNotification: Could not read software licences
                    for equipment '${equipment}' with error '${JSON.stringify(error)}'`,
                );
                return this.softwareLicencesStorageService.loadSoftwareLicencesFromCache(equipment);
            }),
            map((data) => this.convertODataSoftwareLicencesToEquipments(data)),
        );
    }

    private convertODataSoftwareLicencesToEquipments(
        data: ODataSoftwareLicenceSet[] | undefined,
    ): SoftwareLicenceModel[] {
        if (!isDefined(data)) {
            return [];
        }

        return data.map((odataSoftwareLicence) => SoftwareLicenceModel.fromOData(odataSoftwareLicence));
    }
}
